import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import TutorialButton from './common/TutorialButton'; // Import TutorialButton
import { TabulatorFull as Tabulator } from 'tabulator-tables';
import api from '../services/api';
import { tratarErroAPI, logarRequisicao, logarSucesso } from '../utils/errorHandler';
import '../styles/tipomanutencao.css';
import etgLogo from '../assets/etg.png';
import ufmgLogo from '../assets/Ufmg-sem nome-03.png';
import '../styles/home.css';

const TipoManutencaoManager = () => {
    const [dados, setDados] = useState([]);
    const [loading, setLoading] = useState(false);
    const [mensagem, setMensagem] = useState({ tipo: '', texto: '' });
    const [formData, setFormData] = useState({
        id_tp_manutencao: '',
        descr_tp_manutencao: '',
        ativo: 'true',
        id_tp_projeto: '',
    });
    const [apiStatus, setApiStatus] = useState('checking');
    const [lastUpdate, setLastUpdate] = useState(null);
    const [camposComErro, setCamposComErro] = useState([]);
    const [tipoProjetoSearch, setTipoProjetoSearch] = useState('');
    const [tiposProjeto, setTiposProjeto] = useState([]);

    const tabulatorRef = useRef(null);
    const tableRef = useRef(null);

    const mostrarMensagem = useCallback((tipo, texto) => {
        setMensagem({ tipo, texto });
        setTimeout(() => setMensagem({ tipo: '', texto: '' }), 4000);
    }, []);

    const fetchTiposProjeto = useCallback(async () => {
        try {
            const response = await api.get('/tipo-projeto');
            setTiposProjeto(response.data.data || []);
        } catch (error) {
            console.error('Erro ao carregar tipos de projeto:', error);
        }
    }, []);

    const tiposProjetoFiltrados = useMemo(() => {
        if (!tipoProjetoSearch) return tiposProjeto;
        const searchLower = tipoProjetoSearch.toLowerCase();
        return tiposProjeto.filter(t =>
            String(t.id_tp_projeto).toLowerCase().includes(searchLower) ||
            (t.descr_tp_projeto && t.descr_tp_projeto.toLowerCase().includes(searchLower))
        );
    }, [tiposProjeto, tipoProjetoSearch]);

    const verificarAPI = useCallback(async () => {
        try {
            setApiStatus('checking');
            await api.get('/tipo-manutencao');
            setApiStatus('connected');
        } catch (error) {
            setApiStatus('error');
            mostrarMensagem('error', `Erro de conexão: ${error.message}`);
        }
    }, [mostrarMensagem]);

    useEffect(() => {
        verificarAPI();
        fetchTiposProjeto();
    }, [verificarAPI, fetchTiposProjeto]);

    const carregarDados = useCallback(async (showMessage = true) => {
        try {
            setLoading(true);
            const response = await api.get('/tipo-manutencao');
            setDados(response.data.data || []);
            setLastUpdate(new Date());
            if (showMessage) {
                mostrarMensagem('success', `${response.data.data?.length || 0} registros carregados`);
            }
        } catch (error) {
            mostrarMensagem('error', error.message);
            setDados([]);
        } finally {
            setLoading(false);
        }
    }, [mostrarMensagem]);

    const atualizarRegistro = useCallback(async (id, dadosAtualizacao) => {
        logarRequisicao('Atualizar', 'tipo de manutenção', { id, dadosAtualizacao });
        try {
            const response = await api.put(`/tipo-manutencao/${id}`, dadosAtualizacao);
            logarSucesso('Atualizar', 'tipo de manutenção', response.data);
            if (response && response.data) {
                if (tabulatorRef.current) {
                    tabulatorRef.current.updateData([response.data.data]);
                }
                mostrarMensagem('success', 'Registro atualizado com sucesso!');
            } else {
                mostrarMensagem('error', 'Resposta da API inválida ao atualizar.');
            }
        } catch (error) {
            const mensagemErro = tratarErroAPI(error, 'atualizar', 'tipo de manutenção');
            mostrarMensagem('error', mensagemErro);
        }
    }, [mostrarMensagem]);

    const excluirRegistro = useCallback(async (id) => {
        logarRequisicao('Excluir', 'tipo de manutenção', { id });
        try {
            const response = await api.delete(`/tipo-manutencao/${id}`);
            logarSucesso('Excluir', 'tipo de manutenção', response.data);
            setDados(currentDados => currentDados.filter(item => item.id_tp_manutencao !== id));
            mostrarMensagem('success', 'Registro excluído com sucesso!');
        } catch (error) {
            const mensagemErro = tratarErroAPI(error, 'excluir', 'tipo de manutenção');
            mostrarMensagem('error', mensagemErro);
        }
    }, [mostrarMensagem]);

    const colunas = useMemo(() => [
        {
            title: 'Salvar',
            field: 'salvar',
            width: 80,
            hozAlign: 'center',
            formatter: () => '<button class="btn-save"><i class="fas fa-save"></i></button>',
            cellClick: (e, cell) => {
                const data = cell.getRow().getData();
                atualizarRegistro(data.id_tp_manutencao, data);
            },
            headerSort: false
        },
        {
            title: 'Excluir',
            field: 'excluir',
            width: 80,
            hozAlign: 'center',
            formatter: () => '<button class="btn-delete"><i class="fas fa-trash"></i></button>',
            cellClick: (e, cell) => {
                const data = cell.getRow().getData();
                if (window.confirm(`Deseja realmente excluir o registro "${data.id_tp_manutencao}"?`)) {
                    excluirRegistro(data.id_tp_manutencao);
                }
            },
            headerSort: false
        },
        {
            title: 'ID',
            field: 'id_tp_manutencao',
            width: 150,
            headerFilter: 'input',
            headerFilterPlaceholder: 'Filtrar ID...',
            sorter: 'number'
        },
        {
            title: 'Descrição',
            field: 'descr_tp_manutencao',
            headerFilter: 'input',
            headerFilterPlaceholder: 'Filtrar descrição...',
            sorter: 'string',
            editor: 'input'
        },
        {
            title: 'Ativo',
            field: 'ativo',
            headerFilter: 'input',
            headerFilterPlaceholder: 'Filtrar por ativo...',
            sorter: 'boolean',
            editor: 'tickCross',
            formatter: "tickCross"
        },
        {
            title: 'Id tipo de projeto',
            field: 'id_tp_projeto',
            headerFilter: 'input',
            headerFilterPlaceholder: 'Filtrar por ID do projeto...',
            sorter: 'string',
            editor: 'input'
        },
    ], [atualizarRegistro, excluirRegistro]);

    const inicializarTabulator = useCallback(() => {
        if (!tableRef.current) return;
        if (tabulatorRef.current) tabulatorRef.current.destroy();

        tabulatorRef.current = new Tabulator(tableRef.current, {
            data: dados,
            columns: colunas,
            layout: 'fitData',
            pagination: 'local',
            paginationSize: 10,
            paginationCounter: 'rows',
            movableColumns: true,
            resizableRows: true,
            tooltips: true,
            placeholder: 'Nenhum registro encontrado',
            index: 'id_tp_manutencao'
        });

        const style = document.createElement('style');
        style.textContent = `
      .btn-save {
        background: #007bff;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
      }
      .btn-save:hover {
        background: #0056b3;
      }
      .btn-delete {
        background: #dc3545;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
      }
      .btn-delete:hover {
        background: #c82333;
      }
      .btn-back {
        background: transparent;
        border: none;
        font-size: 24px;
        cursor: pointer;
      }
    `;
        document.head.appendChild(style);
    }, [dados, colunas]);

    useEffect(() => {
        if (apiStatus === 'connected') {
            carregarDados(false);
        }
    }, [apiStatus, carregarDados]);

    useEffect(() => {
        if (apiStatus === 'connected' && tableRef.current) {
            if (!tabulatorRef.current) {
                inicializarTabulator();
            } else {
                tabulatorRef.current.setData(dados);
            }
        }
    }, [dados, apiStatus, inicializarTabulator]);

    const adicionarRegistro = async (e) => {
        e.preventDefault();
        const { id_tp_manutencao, descr_tp_manutencao, ativo, id_tp_projeto } = formData;

        // Validação de campos obrigatórios com mensagens específicas
        const camposObrigatorios = [];
        const erros = [];

        if (!id_tp_manutencao) {
            camposObrigatorios.push('ID');
            erros.push('id_tp_manutencao');
        }
        if (!descr_tp_manutencao) {
            camposObrigatorios.push('Descrição');
            erros.push('descr_tp_manutencao');
        }
        if (!id_tp_projeto) {
            camposObrigatorios.push('ID Projeto');
            erros.push('id_tp_projeto');
        }
        if (!ativo) {
            camposObrigatorios.push('Ativo');
            erros.push('ativo');
        }

        if (camposObrigatorios.length > 0) {
            setCamposComErro(erros);
            mostrarMensagem('error', `Por favor, preencha os campos obrigatórios: ${camposObrigatorios.join(', ')}`);
            return;
        }

        setCamposComErro([]);

        const dataToSend = {
            id_tp_manutencao,
            descr_tp_manutencao,
            ativo,
            id_tp_projeto,
        };

        logarRequisicao('Adicionar', 'tipo de manutenção', dataToSend);
        try {
            setLoading(true);
            const response = await api.post('/tipo-manutencao', dataToSend);
            logarSucesso('Adicionar', 'tipo de manutenção', response.data);
            setDados([...dados, response.data.data]);
            setFormData({ id_tp_manutencao: '', descr_tp_manutencao: '', ativo: 'true', id_tp_projeto: '' });
            setTipoProjetoSearch('');
            setCamposComErro([]);
            mostrarMensagem('success', 'Registro adicionado com sucesso!');
        } catch (error) {
            const mensagemErro = tratarErroAPI(error, 'adicionar', 'tipo de manutenção');
            mostrarMensagem('error', mensagemErro);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));

        // Remove o erro do campo quando ele for preenchido
        if (value || type === 'checkbox') {
            setCamposComErro(prev => prev.filter(campo => campo !== name));
        }
    };

    const exportarDados = () => {
        if (tabulatorRef.current) {
            tabulatorRef.current.download('csv', 'tipo_manutencao.csv');
        }
    };

    return (
        <div className="tipo-ambiente-container">
            <div className="header-controls">
                <button className="btn-back" onClick={() => window.history.back()}><i class="fas fa-arrow-left"></i></button>
            </div>

            <div className="header">
                <div className="title-container">
                    <img src={etgLogo} alt="ETG Logo" className="logo" />
                    <h1>Gerenciador de Tipos de Manutenção</h1>
                    <img src={ufmgLogo} alt="UFMG Logo" className="logo" />
                </div>
                <p>Interface para popular e gerenciar a tabela tb_tipo_manutencao</p>
                <div style={{ marginTop: '10px' }}>
                    <TutorialButton videoPath="bdados/tipos manutencao.mov" buttonText="Ver Tutorial Tipos de Manutenção" />
                </div>
            </div>

            {mensagem.texto && (
                <div className="status-container">
                    <div className={`status ${mensagem.tipo}`}>{mensagem.texto}</div>
                </div>
            )}

            <div className="controls">
                <form onSubmit={adicionarRegistro} className="form-inline">
                    <div className="form-group">
                        <label htmlFor="id_tp_manutencao">ID:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
                        <input type="text" id="id_tp_manutencao" name="id_tp_manutencao" value={formData.id_tp_manutencao} onChange={handleInputChange} placeholder="Ex: TM1" maxLength={20} disabled={loading || apiStatus !== 'connected'} className={camposComErro.includes('id_tp_manutencao') ? 'input-error' : ''} />
                    </div>
                    <div className="form-group">
                        <label htmlFor="descr_tp_manutencao">Descrição:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
                        <input type="text" id="descr_tp_manutencao" name="descr_tp_manutencao" value={formData.descr_tp_manutencao} onChange={handleInputChange} placeholder="Ex: Manutenção Corretiva" maxLength={100} disabled={loading || apiStatus !== 'connected'} className={camposComErro.includes('descr_tp_manutencao') ? 'input-error' : ''} />
                    </div>
                    <div className="form-group">
                        <label htmlFor="id_tp_projeto">Id tipo de projeto:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
                        <input
                            type="text"
                            list="tipos-projeto-list"
                            value={tipoProjetoSearch}
                            onChange={(e) => {
                                setTipoProjetoSearch(e.target.value);
                                const match = tiposProjeto.find(t => `${t.id_tp_projeto} - ${t.descr_tp_projeto || ''}` === e.target.value);
                                if (match) {
                                    setFormData(prev => ({ ...prev, id_tp_projeto: match.id_tp_projeto }));
                                    setCamposComErro(prev => prev.filter(c => c !== 'id_tp_projeto'));
                                }
                            }}
                            placeholder="Buscar tipo de projeto..."
                            disabled={loading || apiStatus !== 'connected'}
                            className={camposComErro.includes('id_tp_projeto') ? 'input-error' : ''}
                        />
                        <datalist id="tipos-projeto-list">
                            {tiposProjetoFiltrados.map(t => (
                                <option key={t.id_tp_projeto} value={`${t.id_tp_projeto} - ${t.descr_tp_projeto || ''}`} />
                            ))}
                        </datalist>
                        <input type="hidden" name="id_tp_projeto" value={formData.id_tp_projeto} />
                    </div>
                    <div className="form-group">
                        <label htmlFor="ativo">Ativo:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
                        <select id="ativo" name="ativo" value={formData.ativo} onChange={handleInputChange} disabled={loading || apiStatus !== 'connected'} className={camposComErro.includes('ativo') ? 'input-error' : ''}>
                            <option value="true">Sim</option>
                            <option value="false">Não</option>
                        </select>
                    </div>
                    {/* Linha única: Adicionar, Recarregar, Exportar CSV + aviso de obrigatórios */}
                    <div
                        className="actions-row"
                        style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'nowrap', marginTop: '10px' }}
                    >
                        <button type="submit" className="btn btn-success" disabled={loading || apiStatus !== 'connected'}>
                            {loading ? '⏳ Adicionando...' : '➕ Adicionar'}
                        </button>
                        <button className="btn btn-primary" onClick={() => carregarDados(true)} disabled={loading || apiStatus !== 'connected'}>
                            {loading ? '⏳ Carregando...' : '🔄 Recarregar'}
                        </button>
                        <button className="btn btn-secondary" onClick={exportarDados} disabled={dados.length === 0}>📥 Exportar CSV</button>

                        <div className="info-campos-obrigatorios" style={{ whiteSpace: 'nowrap' }}>
                            <i className="fas fa-info-circle"></i>
                            <span style={{ marginLeft: 6 }}>* indica que os campos são obrigatorios.</span>
                        </div>
                    </div>
                </form>
            </div>

            <div className="stats-bar">
                <div className="stats-item"><strong>{dados.length}</strong> registros carregados</div>
                <div className="stats-item">Última atualização: <strong>{lastUpdate ? lastUpdate.toLocaleTimeString() : 'N/A'}</strong></div>
            </div>

            <div className="tabulator-container">
                <div ref={tableRef}></div>
            </div>

        </div>
    );
};

export default TipoManutencaoManager;
